"use client";

import * as React from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { ArrowUpRight, CircleQuestionMark as HelpCircle } from 'lucide-react';
interface FaqItem {
  id: string;
  question: string;
  answer: string;
}
const faqs: FaqItem[] = [{
  id: "item-1",
  question: "What deck materials perform best in the Decatur climate?",
  answer: "Composite decking, capped polymer boards, and pressure-treated southern yellow pine are the most common options. High-performance composites and capped polymers resist humidity, summer sun, and organic moisture common in Decatur tree canopies with minimal seasonal upkeep."
}, {
  id: "item-2",
  question: "Do deck renovations in Decatur require municipal permits?",
  answer: "Yes. Most structural deck replacements, footprint expansions, elevated multi-level builds, and covered porch additions require plan submittals and building permits through the City of Decatur or DeKalb County building authorities prior to construction."
}, {
  id: "item-3",
  question: "Can an existing deck frame be reused when replacing deck boards?",
  answer: "If the underlying ledger, footings, posts, and joists pass a thorough structural evaluation and meet current spacing codes for modern decking materials, the substructure can often be redecked. If joists show decay or insufficient spacing for composite products, rebuilding the frame ensures long-term safety."
}, {
  id: "item-4",
  question: "How do covered roofs or screened porches integrate with an open deck?",
  answer: "Deck designs can incorporate both covered dining or screened living spaces alongside an open sun deck. Combining sheltered and open zones creates multi-season usability while maintaining natural light inside adjoining interior rooms."
}, {
  id: "item-5",
  question: "What railing and lighting configurations work well on outdoor decks?",
  answer: "Powder-coated aluminum balusters, horizontal cable systems, and composite post sleeves offer clean sightlines into the yard. Low-voltage post-cap lights, stair riser illumination, and under-rail LED strips enhance nighttime visibility and define steps safely."
}];
export default function DeckFaq() {
  return <section id="deckremodelingfaq" data-nav="light" className="relative bg-background text-foreground py-20 md:py-28 px-4 sm:px-6 lg:px-8 border-b border-border/40">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-medium mb-4">
            <HelpCircle className="size-3.5 text-chart-1" />
            <span>Common Questions</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif tracking-tight text-foreground">
            Deck remodeling questions & answers
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto font-sans leading-relaxed">
            Planning an outdoor upgrade involves decisions on layout, materials, and municipal planning. Here is what to know about building or upgrading a deck.
          </p>
        </div>

        <div className="bg-card/90 border border-border/70 rounded-xl p-4 sm:p-8 shadow-sm backdrop-blur-xs">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => <AccordionItem key={faq.id} value={faq.id} data-index={index} className="border-b border-border/60 py-1 last:border-b-0">
                <AccordionTrigger className="text-left font-serif text-lg md:text-xl font-medium text-foreground hover:no-underline hover:text-chart-1 transition-colors py-4">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="font-sans text-sm sm:text-base text-muted-foreground leading-relaxed pt-1 pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>

        <div className="mt-12 text-center flex flex-col sm:flex-row items-center justify-center gap-4">
          <p className="text-sm text-muted-foreground">
            Have a specific layout or material in mind for your outdoor space?
          </p>
          <Button asChild variant="outline" size="sm" className="rounded-md gap-1.5">
            <Link to="/ContactPage">
              <span>Contact our team</span>
              <ArrowUpRight className="size-3.5" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}